import React, { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import "./CoreFlash.css";

/**
 * Global toaster + ripple effect for inline param edits.
 *
 * Listens for `window.dispatchEvent(new CustomEvent('core:param-updated', { detail: {key, label}}))`
 * which `EditableValue` fires on every successful save.
 *
 * Side-effects:
 *   1. Stacks a small bottom-right toast for ~2.6s with the label being recomputed.
 *   2. Finds every DOM element marked `data-paramkey="<key>"` and adds the
 *      `core-flash` class for ~1s so the user sees the dashboard "re-think".
 */
export default function ParamUpdateToaster() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const handler = (e) => {
      const { key, label } = e.detail || {};
      const id = `${Date.now()}-${Math.random()}`;
      const message = `${label || key} updated · re-running intelligence engine`;
      setToasts((t) => [...t, { id, message, stage: "saving" }]);

      // Flash every tile carrying data-paramkey={key} (and explicit data-flashkey
      // for tiles that derive from the same param but render under a different key).
      try {
        const selector = `[data-paramkey="${key}"], [data-flashkey="${key}"]`;
        document.querySelectorAll(selector).forEach((el) => {
          el.classList.remove("core-flash");
          // Force reflow so the animation restarts even on rapid re-edits.
          // eslint-disable-next-line no-unused-expressions
          el.offsetWidth;
          el.classList.add("core-flash");
          setTimeout(() => el.classList.remove("core-flash"), 1050);
        });
      } catch (_e) { /* DOM not ready — toast still fires */ }

      // After 700ms we tick the toast to "done" so the spinner becomes a tick.
      setTimeout(() => {
        setToasts((cur) => cur.map((t) => t.id === id ? { ...t, stage: "done" } : t));
      }, 700);
      // Drop it after 2.6s.
      setTimeout(() => setToasts((cur) => cur.filter((t) => t.id !== id)), 2600);
    };
    window.addEventListener("core:param-updated", handler);
    return () => window.removeEventListener("core:param-updated", handler);
  }, []);

  if (toasts.length === 0) return null;
  return (
    <div data-testid="param-update-toaster"
      style={{
        position: "fixed",
        bottom: 18,
        right: 18,
        zIndex: 80,
        display: "flex",
        flexDirection: "column-reverse",
        gap: 8,
        pointerEvents: "none",
      }}>
      {toasts.map((t) => (
        <div key={t.id} data-testid={`toast-${t.id}`}
          style={{
            background: t.stage === "done" ? "#059669" : "#1E3A5F",
            color: "white",
            padding: "10px 14px",
            borderRadius: 8,
            fontSize: 12.5,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 280,
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            transition: "all 250ms ease",
          }}>
          {t.stage === "done"
            ? <Check size={14} />
            : <Loader2 size={14} className="animate-spin" />}
          {t.message}
        </div>
      ))}
    </div>
  );
}
